import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { CurrencyCodeSchema } from 'types/currency';
import { CommunicationLocaleSchema } from 'types/locale';
import { z } from 'zod/v4';

const URL = '/company_profile';

// The company's currency conversion date is a fixed two-option enum. Tightened
// from the legacy `z.string()` read schema (audit rule).
export const CurrencyConversionDateSchema = z.enum(['ISSUE_DATE', 'CURRENT_DATE']);

const CompanyProfileAddressSchema = z.object({
  streetName: z.string(),
  streetNumber: z.string(),
  streetBox: z.string().nullable(),
  zipCode: z.string(),
  city: z.string(),
  countryCode: z.string(),
});

export const CompanyProfileSchema = z.object({
  name: z.string(),
  companyEmail: z.string(),
  // Read-only, delegated from the company; an empty string when unset.
  vatNumber: z.string(),
  locale: CommunicationLocaleSchema,
  currency: CurrencyCodeSchema,
  currencyConversionDate: CurrencyConversionDateSchema,
  phoneNumber: z.string(),
  address: CompanyProfileAddressSchema,
});

export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;

export async function loadCompanyProfileQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = CompanyProfileSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'companyProfile');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadCompanyProfile(options: QueryOptions<CompanyProfile> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<CompanyProfile, ApiError>({
    queryKey: ['companyProfile'],
    queryFn: () => loadCompanyProfileQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<CompanyProfile, ApiError, 'companyProfile'>(
    'companyProfile',
    queryResult,
  );
}
